import React, { useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { EmptyState } from "../../UI/States";

/**
 * Purpose-built table for the service request views — plain Tailwind instead
 * of material-table, so it matches the app-card look, follows dark mode for
 * free, and the search/sort/pagination chrome stays legible.
 *
 * Column shape:
 *   {
 *     key,                  // unique id
 *     title,                // header text
 *     render: (row) => jsx, // cell content
 *     sortValue: (row) =>   // comparable value; omit to disable sorting
 *     searchValue: (row) => // text for the search box; omit to exclude
 *     align?: "right",
 *     width?: tailwind class for the header (e.g. "w-24")
 *     stopClick?: true,     // cell hosts its own controls (dropdowns)
 *   }
 */
const PAGE_SIZES = [10, 25, 50, 100];

const compare = (a, b) => {
  if (a === b) return 0;
  if (a === null || a === undefined || a === "") return -1;
  if (b === null || b === undefined || b === "") return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const DataTable = ({
  columns,
  rows,
  // Superset to search across. When set and the search box has text, the
  // table matches against THESE rows instead of `rows` — so a search finds
  // a request no matter which status/assignee filters are active — and a
  // hint in the toolbar says so. Omit to search only the visible rows.
  allRows,
  loading = false,
  onRowClick,
  initialSort, // { key, dir: "asc" | "desc" }
  // Restored view, read once on mount: { query?, sort?, page? (0-based),
  // pageSize? }. onStateChange hears every change so the page can keep it
  // somewhere that survives Back and refresh (the shop dashboard uses the URL).
  initialState,
  onStateChange,
  emptyTitle = "Nothing here",
  emptyBody = "No rows match.",
  searchPlaceholder = "Search…",
}) => {
  const [query, setQuery] = useState(initialState?.query || "");
  const [sort, setSort] = useState(initialState?.sort || initialSort || null);
  const [pageSize, setPageSize] = useState(
    PAGE_SIZES.includes(initialState?.pageSize) ? initialState.pageSize : 25
  );
  const [page, setPage] = useState(Math.max(0, initialState?.page || 0));

  useEffect(() => {
    if (onStateChange) onStateChange({ query, sort, page, pageSize });
    // Only the table's own changes; a new callback identity isn't one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sort, page, pageSize]);

  const toggleSort = (column) => {
    if (!column.sortValue) return;
    setPage(0);
    setSort((prev) =>
      prev?.key === column.key
        ? { key: column.key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key: column.key, dir: "asc" }
    );
  };

  const needle = query.trim().toLowerCase();
  const searchingAll = Boolean(needle && allRows);

  const filtered = useMemo(() => {
    if (!needle) return rows || [];
    const list = (searchingAll ? allRows : rows) || [];
    const searchable = columns.filter((column) => column.searchValue);
    return list.filter((row) =>
      searchable.some((column) =>
        String(column.searchValue(row) ?? "")
          .toLowerCase()
          .includes(needle)
      )
    );
  }, [rows, allRows, needle, searchingAll, columns]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((item) => item.key === sort.key);
    if (!column?.sortValue) return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const result = compare(column.sortValue(a), column.sortValue(b));
      return sort.dir === "asc" ? result : -result;
    });
    return copy;
  }, [filtered, sort, columns]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const from = sorted.length === 0 ? 0 : safePage * pageSize + 1;
  const to = Math.min(sorted.length, (safePage + 1) * pageSize);

  return (
    <div className="app-card overflow-hidden rounded-2xl border">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b app-divider px-4 py-3">
        <span className="app-muted text-xs font-medium tabular-nums">
          {loading
            ? "Loading…"
            : `${sorted.length.toLocaleString()} ${
                sorted.length === 1 ? "result" : "results"
              }`}
        </span>
        <div className="flex w-full flex-wrap items-center justify-end gap-3 sm:w-auto">
        {searchingAll && (
          <span className="rounded-full bg-sky-500/15 px-2.5 py-1 text-xs font-medium text-sky-700 dark:text-sky-300">
            Searching all requests — status &amp; assignee filters ignored
          </span>
        )}
        <div className="relative w-full sm:w-72">
          <SearchIcon
            fontSize="small"
            className="app-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
          />
          <input
            type="text"
            value={query}
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
            className="app-input w-full rounded-full border py-2 pl-10 pr-9 text-sm transition"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setPage(0);
              }}
              className="app-muted absolute right-1.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full hover:bg-[color:var(--app-hover)] hover:text-[color:var(--app-text)]"
              aria-label="Clear search"
            >
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="app-table">
          <thead>
            <tr>
              {columns.map((column) => {
                const active = sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    aria-sort={
                      active
                        ? sort.dir === "asc"
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                    className={`${column.width || ""} ${
                      column.align === "right" ? "!text-right" : ""
                    }`}
                  >
                    {column.sortValue ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column)}
                        className={`inline-flex items-center gap-1 transition hover:text-[color:var(--app-text)] ${
                          active ? "text-[color:var(--app-text)]" : ""
                        }`}
                      >
                        {column.title}
                        {active ? (
                          sort.dir === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon
                            sx={{ fontSize: 13 }}
                            className="opacity-40"
                          />
                        )}
                      </button>
                    ) : (
                      column.title
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 6 }).map((_, index) => (
                <tr key={`skeleton-${index}`}>
                  {columns.map((column) => (
                    <td key={column.key} className="!py-3.5">
                      <div className="app-skeleton h-3.5 w-4/5" />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading && pageRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="!p-0">
                  <EmptyState
                    icon={SearchIcon}
                    title={emptyTitle}
                    description={emptyBody}
                  />
                </td>
              </tr>
            )}

            {!loading &&
              pageRows.map((row, index) => (
                <tr
                  key={row._id || row.id || index}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? "cursor-pointer" : ""}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      onClick={
                        // Cells that host their own controls (assignee
                        // dropdowns) must not also open the row.
                        column.stopClick
                          ? (event) => event.stopPropagation()
                          : undefined
                      }
                      className={column.align === "right" ? "text-right" : ""}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t app-divider px-4 py-2.5 text-sm">
        <label className="app-muted flex items-center gap-2 text-xs">
          Rows per page
          <select
            value={pageSize}
            onChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(0);
            }}
            className="app-input rounded-lg border px-2 py-1 text-xs"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <span className="app-muted mr-2 text-xs tabular-nums">
            {from}–{to} of {sorted.length.toLocaleString()}
          </span>
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setPage(safePage - 1)}
            className="app-icon-btn disabled:pointer-events-none disabled:opacity-30"
            aria-label="Previous page"
          >
            <ChevronLeftIcon fontSize="small" />
          </button>
          <button
            type="button"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage(safePage + 1)}
            className="app-icon-btn disabled:pointer-events-none disabled:opacity-30"
            aria-label="Next page"
          >
            <ChevronRightIcon fontSize="small" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
